import { useCallback, useMemo } from "react";
import type {
  CartonDigitalData,
  CartonDigitalMetrics,
  CartonDigitalModalProps,
  CartonGridItem,
  UseCartonDigitalModalReturn,
} from "./types";

const CARTON_BASE_CLASS_NAME =
  "h-[60px] relative md:h-[70px] w-[100 / 5%] flex flex-col justify-center items-center rounded";

const calcularPagoDiarioSeguro = (datos: CartonDigitalData): number => {
  return Number(datos.pagoDiario ?? 0) || 1;
};

const calcularValorPicoDisplay = (
  totalAbono: number,
  pagoDiario: number
): number | null => {
  const valorPico = totalAbono % pagoDiario;

  return valorPico !== 0 ? Number(valorPico.toFixed(1)) : null;
};

const crearCartonItemClassName = (
  numero: number,
  cuotasPagadas: number,
  valorPicoDisplay: number | null
): string => {
  if (valorPicoDisplay !== null && numero === cuotasPagadas + 1) {
    return `${CARTON_BASE_CLASS_NAME} bg-yellow-500 border-none font-semibold`;
  }

  if (numero <= cuotasPagadas) {
    return `${CARTON_BASE_CLASS_NAME} bg-green-500 text-white`;
  }

  return `${CARTON_BASE_CLASS_NAME} border-red-500 border text-black`;
};

const calcularCartonDigitalMetrics = (
  datos: CartonDigitalData
): CartonDigitalMetrics => {
  const cuotasPactadas = Number(datos.cuotasPactadas ?? 0);
  const pagoDiarioSeguro = calcularPagoDiarioSeguro(datos);
  const totalAbono = Number(datos.totalAbono ?? 0);
  const montoPendiente = Number(datos.pagoDiario ?? 0);
  const cuotasPagadas = Math.floor(totalAbono / pagoDiarioSeguro);
  const valorPicoDisplay = calcularValorPicoDisplay(totalAbono, pagoDiarioSeguro);

  return {
    cuotasPagadas,
    cuotasPactadas,
    montoPendiente,
    pagoDiarioSeguro,
    totalAbono,
    valorPicoDisplay,
  };
};

const crearCartonItems = ({
  cuotasPagadas,
  cuotasPactadas,
  montoPendiente,
  valorPicoDisplay,
}: CartonDigitalMetrics): CartonGridItem[] => {
  return Array.from({ length: cuotasPactadas }, (_, index) => {
    const numero = index + 1;
    const isPagado = numero <= cuotasPagadas;
    const isValorPico =
      valorPicoDisplay !== null && numero === cuotasPagadas + 1;

    return {
      className: crearCartonItemClassName(
        numero,
        cuotasPagadas,
        valorPicoDisplay
      ),
      isPagado,
      isValorPico,
      montoPendiente,
      numero,
      valorPicoDisplay,
    };
  });
};

export const useCartonDigitalModal = ({
  datos,
  setMostrarCartonDigital,
}: CartonDigitalModalProps): UseCartonDigitalModalReturn => {
  const cartonMetrics = useMemo(
    () =>
      calcularCartonDigitalMetrics({
        cuotasPactadas: datos.cuotasPactadas,
        pagoDiario: datos.pagoDiario,
        totalAbono: datos.totalAbono,
      }),
    [datos.cuotasPactadas, datos.pagoDiario, datos.totalAbono]
  );

  const cartonItems = useMemo(
    () => crearCartonItems(cartonMetrics),
    [cartonMetrics]
  );

  const handleClose = useCallback(() => {
    setMostrarCartonDigital(false);
  }, [setMostrarCartonDigital]);

  return {
    cartonItems,
    handleClose,
  };
};
